"use client";
import { DollarSign, Loader2Icon, Repeat } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useApiGet } from "@/react-queries/custom-request";
import {
    getAvaliableBalance,
    getLockedBalance,
} from "@/react-queries/queries/customers";
import {
    AvaliableBalanceTypes,
    LockedBalanceTypes,
} from "@/react-queries/queries/customers/types";
import { useTranslations } from "next-intl";
import { createPortal } from "react-dom";
import "./balance.scss"
import BalanceItem from "./BalanceMobile/BalanceItem";

const active = "bg-indigo-950 bg-opacity-100 pointer-events-none select-none";

const Balance = () => {
    const translate = useTranslations("Header.balance");
    const [isOpen, setIsOpen] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const balanceRef = useRef<HTMLDivElement>(null);
    const balanceButtonRef = useRef<HTMLButtonElement>(null);

    const buttonPosition = balanceButtonRef.current ?
        balanceButtonRef.current.getBoundingClientRect() :
        { top: 0, left: 0, height: 0, width: 0 };
    const {
        data: lockedBalance,
        isLoading: lockedLoading,
        isError: lockedError,
        refetch: retryLocked,
    } = useApiGet<LockedBalanceTypes[]>(["locked_balance"], getLockedBalance);
    const {
        data: avaliableBalance,
        isLoading: avaliableLoading,
        isError: avaliableError,
        refetch: retryAvaliable,
    } = useApiGet<AvaliableBalanceTypes[]>(
        ["avaliable_balance"],
        getAvaliableBalance
    );
    const [balanceType, setBalanceType] = useState<"locked" | "avaliable">(
        "locked"
    ); const [balance, setBalance] = useState<
        LockedBalanceTypes[] | AvaliableBalanceTypes[]
    >();
    useClickOutside(balanceRef, () => setIsOpen(false)); useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (lockedBalance && avaliableBalance) {
            if (balanceType === "locked") {
                // @ts-ignore
                setBalance(lockedBalance);
            } else {
                // @ts-ignore
                setBalance(avaliableBalance);
            }
        }
    }, [balanceType, lockedBalance, avaliableBalance]);

    if (!isMounted) {
        return (
            <div className="relative z-50">
                <Button
                    className="balance_btn"
                    disabled
                >
                    <DollarSign />
                </Button>
            </div>
        );
    }

    return (
        // @ts-ignore
        <div className="relative z-50">
            <Button
                ref={balanceButtonRef}
                className="balance_btn"
                onClick={() => setIsOpen(!isOpen)}
            >
                <DollarSign />
            </Button>            {isOpen && isMounted &&
                createPortal(
                    <div ref={balanceRef}
                        className={cn(
                            `balance-animate absolute z-[999] origin-top-right transition-transform overflow-hidden rounded-2xl mt-2  w-[300px] bg-surface border border-surface-border top-[100px] shadow-md`,

                        )}
                        style={{ top: (buttonPosition.top + buttonPosition.height), left: buttonPosition.left - 150 + buttonPosition.width / 2 }}

                    >

                        <div className="flex flex-col gap-4 p-4 text-white bg-[#405189] font-bold">
                            <p>{translate("title")}</p>
                            <div className="flex gap-4">
                                <span
                                    onClick={() => setBalanceType("locked")}
                                    className={cn(
                                        "w-full text-center text-sm font-normal rounded-sm p-2 bg-surface/50 border border-surface-border hover:opacity-80",
                                        balanceType === "locked" && active
                                    )}
                                    role="button"
                                >
                                    {translate("locked")}
                                </span>
                                <span
                                    onClick={() => setBalanceType("avaliable")}
                                    className={cn(
                                        "w-full text-center text-sm font-normal rounded-sm p-2 bg-surface/50 border border-surface-border hover:opacity-80",
                                        balanceType === "avaliable" && active
                                    )}
                                    role="button"
                                >
                                    {translate("available")}
                                </span>
                            </div>
                        </div>
                        <div className="even:[&>div]:bg-surface/50 max-h-[250px] overflow-y-auto scrollbar">
                            {balance && balance.length > 0 ? (
                                balance.map((b, i) => (
                                    <BalanceItem
                                        key={`${balanceType}-${b.currency_code}-${b.id}-${i}`}
                                        amount={b.balance}
                                        amountUSD={b.usd_amount}
                                        fullName={b.name}
                                        shortName={b.currency_code}
                                        img={b.currency_code.toLowerCase()}
                                        type={b.type as "crypto" | "currency"}
                                    />
                                ))
                            ) : lockedError || avaliableError ? (
                                <div className="bg-red-400 bg-opacity-25 text-white flex items-center p-4 text-xs gap-2">
                                    An error occured while getting {balanceType} balance
                                    <button
                                        className="bg-red-700 text-white rounded-sm shrink-0 p-2 flex items-center gap-2"
                                        onClick={() => {
                                            if (balanceType === "locked") return retryLocked();
                                            return retryAvaliable();
                                        }}
                                    >
                                        {avaliableLoading || lockedLoading ? (
                                            <Loader2Icon
                                                width={15}
                                                height={15}
                                                className="shrink-0 animate-spin"
                                            />
                                        ) : (
                                            <>
                                                Try again
                                                <Repeat width={15} height={15} className="shrink-0" />
                                            </>
                                        )}
                                    </button>
                                </div>
                            ) : (
                                <div className="w-full p-4 text-center">No balance</div>
                            )}
                        </div>                        </div>,
                    document.body
                )}
        </div>
    );
};

export default Balance;
